/**
 * SelectControl — a native <select> bound to an EnumParameter.
 *
 * Built for enum params with too many options to comfortably fit as a row of
 * buttons (WaveSelect works well up to ~5 options; beyond that it either wraps
 * awkwardly or pushes the module panel absurdly wide). Tempo-synced divisions
 * with their 9 entries are the motivating case.
 */

import type { EnumParameter } from '@ac-slayer/synth-core';

export interface SelectControlOptions<T extends string> {
  readonly param: EnumParameter<T>;
  readonly label?: string;
}

export class SelectControl<T extends string> {
  readonly element: HTMLElement;
  private readonly param: EnumParameter<T>;
  private readonly select: HTMLSelectElement;
  private readonly unsubscribe: () => void;

  constructor(opts: SelectControlOptions<T>) {
    this.param = opts.param;

    this.element = document.createElement('div');
    this.element.className = 'select-control';

    const label = document.createElement('div');
    label.className = 'label';
    label.textContent = opts.label ?? this.param.name;
    this.element.appendChild(label);

    this.select = document.createElement('select');
    this.select.className = 'select-control-input mono';
    this.select.setAttribute('aria-label', opts.label ?? this.param.name);
    for (const option of this.param.options) {
      const o = document.createElement('option');
      o.value = option;
      o.textContent = this.param.formatter(option);
      if (option === this.param.value) o.selected = true;
      this.select.appendChild(o);
    }
    this.select.addEventListener('change', () => {
      this.param.set(this.select.value as T, 'ui');
    });
    this.element.appendChild(this.select);

    this.unsubscribe = this.param.subscribe((v) => {
      if (this.select.value !== v) this.select.value = v;
    });
  }

  dispose(): void {
    this.unsubscribe();
  }
}
